import { Link } from "@tanstack/react-router";
import { clsx } from "clsx";

import type { EntranceMode } from "@/routes";

/**
 * The three-entrance switcher per Part 11 §1 of the visual identity
 * reference: three text labels (Query / Graph / Catalog), 12px font,
 * 12px gap, no icons. Active label renders in `ink-primary` at
 * font-weight 500; inactive labels render in `ink-tertiary`. No
 * underline, no pill, no border — text only.
 */
const ENTRANCES: readonly { mode: EntranceMode; label: string }[] = [
  { mode: "query", label: "Query" },
  { mode: "graph", label: "Graph" },
  { mode: "catalog", label: "Catalog" },
];

export interface HeaderSwitcherProps {
  /** Currently-active entrance, or `null` when no entrance is active —
   * e.g. on detail surfaces (`/search`, `/finding-aid/$id`,
   * `/document/$id`) where the user is past the entrance step. */
  active: EntranceMode | null;
}

export function HeaderSwitcher({ active }: HeaderSwitcherProps) {
  return (
    <nav aria-label="Switch entrance" className="flex items-center gap-2 text-meta sm:gap-3">
      {ENTRANCES.map(({ mode, label }) => {
        const isActive = mode === active;
        return (
          <Link
            key={mode}
            to="/"
            search={(prev) => ({ ...prev, mode })}
            className={clsx(
              "rounded-badge transition-colors",
              isActive
                ? "font-medium text-ink-primary"
                : "text-ink-tertiary hover:text-ink-secondary",
            )}
            aria-current={isActive ? "page" : undefined}
          >
            {label}
          </Link>
        );
      })}
    </nav>
  );
}
